import { useState } from 'react';
import { dateHeureDeTunis } from '@rh/shared';
import { MESSAGE_ABANDON, modeRefus, texteSaisie } from '../horsligne/textes';
import type { EnvoiContact } from '../horsligne/types';

/**
 * Reprise d'une saisie refusée par le serveur (docs/UI.md § Écran 3, mode hors
 * ligne), en tête du panneau : le message de l'API, puis ce qu'on peut en
 * faire. Le formulaire, rempli depuis la saisie refusée, reste dessous.
 */

const MESSAGE_ANONYMISEE =
  'Fiche anonymisée entre-temps (conservation des données) : la saisie ne peut plus être enregistrée, ' +
  'son contenu a été effacé de ce téléphone.';
const MESSAGE_SUPPRIMEE =
  'Ce contact n’existe plus (fiche supprimée) : le contenu de la saisie a été effacé de ce téléphone.';

function detailsLisibles(details: unknown): string[] {
  if (!Array.isArray(details)) return [];
  return details
    .map((detail) => {
      if (typeof detail === 'string') return detail;
      const { champ, message } = (detail ?? {}) as { champ?: unknown; message?: unknown };
      if (typeof message !== 'string') return null;
      return typeof champ === 'string' && champ ? `${champ} : ${message}` : message;
    })
    .filter((texte): texte is string => texte !== null);
}

export function RefusEnvoi({
  envoi,
  enLigne,
  consigneBareme = null,
  onRenvoyerHeureEnvoi,
  onEnvoyerEvaluationSeule,
  onAbandonner,
  onCompris,
}: {
  envoi: EnvoiContact;
  enLigne: boolean;
  /** Barème relu et note recalculée (BAREME_CHANGE), fournie par le panneau. */
  consigneBareme?: string | null;
  onRenvoyerHeureEnvoi(): void;
  onEnvoyerEvaluationSeule(): void;
  onAbandonner(): void;
  onCompris(): void;
}) {
  const [copie, setCopie] = useState<'faite' | 'manuelle' | null>(null);
  const refus = envoi.refus;
  if (!refus) return null;
  const mode = modeRefus(refus);
  const details = detailsLisibles(refus.details);
  const texte = envoi.saisie ? texteSaisie(envoi.saisie) : '';

  async function copier(): Promise<void> {
    try {
      await navigator.clipboard.writeText(texte);
      setCopie('faite');
    } catch {
      // Presse-papiers refusé (contexte non sécurisé) : le texte s'affiche pour une copie à la main.
      setCopie('manuelle');
    }
  }

  function abandonner(): void {
    if (window.confirm(MESSAGE_ABANDON)) onAbandonner();
  }

  let consigne: string;
  switch (mode) {
    case 'efface':
      consigne = refus.code === 'FICHE_ANONYMISEE' ? MESSAGE_ANONYMISEE : MESSAGE_SUPPRIMEE;
      break;
    case 'bareme':
      consigne = consigneBareme ?? 'Le barème a changé : vérifiez la note, puis validez à nouveau.';
      break;
    case 'horodatage':
      consigne = 'L’heure du téléphone semble fausse.';
      break;
    case 'clos':
      consigne =
        'Ce contact est clôturé : cette saisie ne peut plus y être enregistrée. Relisez-la ; vous pouvez la ' +
        'reporter dans un nouveau contact.';
      break;
    case 'bloque':
      consigne = 'Votre saisie ne peut pas être enregistrée en l’état.';
      break;
    default:
      consigne = 'Corrigez les points signalés, puis renvoyez.';
  }

  return (
    <section className="refus-envoi" role="alert">
      <h3>Saisie refusée le {dateHeureDeTunis(refus.le)}</h3>
      {mode !== 'efface' && <p className="refus-envoi__message">{refus.message}</p>}
      {details.length > 0 && (
        <ul>
          {details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      )}
      <p>{consigne}</p>
      <div className="actions">
        {mode === 'efface' && (
          <button type="button" className="bouton" onClick={onCompris}>
            J’ai compris
          </button>
        )}
        {mode === 'horodatage' && (
          <button type="button" className="bouton" onClick={onRenvoyerHeureEnvoi}>
            Renvoyer avec l’heure d’envoi
          </button>
        )}
        {mode === 'clos' && refus.action === 'envoyer_evaluation_seule' && (
          <button type="button" className="bouton" onClick={onEnvoyerEvaluationSeule}>
            Envoyer seulement l’évaluation
          </button>
        )}
        {(mode === 'clos' || mode === 'bloque') && texte && (
          <button type="button" className="bouton-second" onClick={() => void copier()}>
            {copie === 'faite' ? 'Saisie copiée' : 'Copier ma saisie'}
          </button>
        )}
        {mode !== 'efface' && (
          <button type="button" className="bouton-second bouton-danger" onClick={abandonner}>
            Abandonner cette saisie
          </button>
        )}
      </div>
      {copie === 'manuelle' && (
        <textarea className="copie-manuelle" readOnly rows={6} value={texte} aria-label="Saisie à copier" />
      )}
      {!enLigne && mode !== 'efface' && (
        <p className="mention">Hors ligne : le renvoi partira au retour du réseau.</p>
      )}
    </section>
  );
}
